import React, { useEffect, useState } from "react";
import { Box, Grid, TextField } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import useFetchRoleById from "../../hooks/useFetchRoleById";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import {
  IconBtnEditBase,
  IconAssignPermissionsBase,
} from "../../common/utils/icons";
import Loader from "../../components/Molecules/Loader/Loader";
import { AssignPermissionsModal } from "../../components/Roles";
import { Tooltip, IconButton } from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { isAdminRole } from "../../types/role";

const ViewRole: React.FC = () => {
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { id: roleId } = useParams();

  // Permission checks for role actions
  const canEditRole = hasPermission("user_role", "edit");
  const canAssignPermissions = hasPermission("casbin_policies", "view");
  const [formData, setFormData] = useState<any>({
    role_name: "",
    role_description: "",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [permissionsModalOpen, setPermissionsModalOpen] =
    useState<boolean>(false);

  // Fetch role data
  const [roleData] = useFetchRoleById({
    setIsLoading,
    roleId: roleId || null,
  });

  useEffect(() => {
    if (roleData) {
      setFormData({
        role_name: roleData?.role_name,
        role_description: roleData?.role_description,
      });
      setCurrentBreadcrumbPage({
        name: roleData?.role_name,
        id: roleData?.id,
      });
    }
    return () => {
      setCurrentBreadcrumbPage({ name: "", id: null });
    };
  }, [roleData, setCurrentBreadcrumbPage]);

  // Handle assign permissions
  const handleAssignPermissions = () => {
    setPermissionsModalOpen(true);
  };

  // Handle permissions modal close
  const handlePermissionsModalClose = () => {
    setPermissionsModalOpen(false);
  };

  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}
      <Box>
        <Box className="text-box-card-white">
          <Box className="text-box-header">
            <h3>Role Details</h3>
            <Box sx={{ display: "flex", gap: 1 }}>
              {canAssignPermissions && !isAdminRole(roleData) && (
                <Tooltip title="Assign Permissions" placement="top" arrow>
                  <IconButton
                    className="btn-nostyle icon-btn-edit"
                    onClick={handleAssignPermissions}
                    disabled={!roleData}
                    aria-label="Assign permissions to role"
                    data-testid="assign-permissions-btn"
                  >
                    <IconAssignPermissionsBase />
                  </IconButton>
                </Tooltip>
              )}
              {canAssignPermissions && isAdminRole(roleData) && (
                <Tooltip
                  title="Admin role permissions cannot be modified"
                  placement="top"
                  arrow
                >
                  <IconButton
                    className="btn-nostyle icon-btn-edit"
                    disabled
                    sx={{ opacity: 0.5 }}
                    aria-label="Admin role permissions cannot be modified"
                    data-testid="assign-permissions-disabled-btn"
                  >
                    <IconAssignPermissionsBase />
                  </IconButton>
                </Tooltip>
              )}
              {canEditRole && (
                <Tooltip title="Edit Role" placement="top" arrow>
                  <button
                    className="btn-nostyle icon-btn-edit"
                    onClick={() => navigate(`/roles/edit/${roleId}`)}
                  >
                    <IconBtnEditBase
                      width={32}
                      height={32}
                      ariaLabel="Edit role"
                    />
                  </button>
                </Tooltip>
              )}
            </Box>
          </Box>

          <Grid container rowSpacing={1.5} columnSpacing={2.5}>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">Role Name</label>
              <TextField
                type="text"
                name="role_name"
                value={formData?.role_name}
                className="form-control read-only"
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">Description</label>
              <TextField
                type="text"
                name="role_description"
                value={formData?.role_description || "No description provided"}
                className="form-control read-only"
                disabled
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {roleData && (
        <AssignPermissionsModal
          open={permissionsModalOpen}
          onClose={handlePermissionsModalClose}
          roleId={roleData.id}
          roleName={roleData.role_name}
          roleType={roleData.role_type}
        />
      )}
    </>
  );
};

export default ViewRole;
